import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import ExitPopup1120S from '@/components/ui/ExitPopup1120s';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120SMailingAddress = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 1120-S Mailing Address | Where to File S Corp Taxes"
          description="Find the correct IRS mailing address for Form 1120-S. View state-wise filing addresses, PDS rules, and tips to avoid IRS delays."
          keywords=""
          canonical="resources/business-tax/form-1120-s-mailing-address/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <ExitPopup1120S />
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1120-S</span>
          </span>{' '}
          Mailing Address
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/form-1120-s-mailing-address/"
                  />
                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1120-S Mailing Address at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="1"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Find the correct IRS mailing address
                        </strong>{' '}
                        for Form 1120-S based on your S corporation's business
                        location, total assets, and Schedule M-3 filing
                        requirement.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="2"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Learn when to use USPS or an IRS-approved Private
                          Delivery Service (PDS)
                        </strong>{' '}
                        and how to avoid common mailing mistakes that can delay
                        processing.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <data
                        itemProp="position"
                        value="3"
                        style={{ display: 'none' }}
                      />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Understand your filing options,
                        </strong>{' '}
                        including Form 1120-S mailing deadlines, extension
                        information, and when e-filing may be a faster
                        alternative.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Finding the correct{' '}
                  <span style={{ fontWeight: 600 }}>
                    Form 1120-S mailing address
                  </span>{' '}
                  is essential when filing an S corporation tax return by mail.
                  The IRS assigns different filing locations based on the
                  corporation&apos;s{' '}
                  <span style={{ fontWeight: 600 }}>
                    principal business location, total assets, and Schedule M-3
                    filing requirement
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This resource page helps you quickly identify{' '}
                  <span style={{ fontWeight: 600 }}>
                    where to file Form 1120-S
                  </span>
                  , avoid mailing errors, and ensure your return is processed
                  without delays. Businesses looking for a faster option may
                  also choose to{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 1120-S online
                  </Link>{' '}
                  instead of mailing.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                  }}
                  className="gradient-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '20px !important',
                      }}
                    >
                      The IRS Encourages E-Filing for Faster, More Secure
                      Processing.
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 1120-S now with TaxZerone and easily share
                      Schedule K-1s with your shareholders.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#0bf473',
                        color: '#333',
                        fontSize: 18,
                        fontWeight: 600,
                        '&:hover, &:focus, &:active': {
                          backgroundColor: '#fff',
                        },
                      }}
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120smail`}
                    >
                      E-file Form 1120-S
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: '#fff3b1',
                      py: 1,
                      px: 2,
                      border: '2px dashed #000',
                      color: '#000',
                      lineHeight: 1.3,
                    }}
                  >
                    <b>Grab 10% off</b> your filing fee — just enter{' '}
                    <b style={{ fontSize: 18, color: '#c02121' }}>TZOBIZ10</b>{' '}
                    at checkout.
                  </Box>
                </Box>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why the Correct Form 1120-S Mailing Address Matters
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to File Your Taxes for Form 1120-S
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#private" sx={{ color: '#0202ff' }}>
                          Private Delivery Services (PDS) for Form 1120-S
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#important" sx={{ color: '#0202ff' }}>
                          Important Notes for Form 1120-S Mailing
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#deadline" sx={{ color: '#0202ff' }}>
                          Form 1120-S Filing Deadline
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#mailing" sx={{ color: '#0202ff' }}>
                          Mailing vs E-Filing Form 1120-S
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Mailing Mistakes to Avoid
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="why"
                >
                  Why the Correct Form 1120-S Mailing Address Matters
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS uses different mailing addresses depending on:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Whether a payment is included </li>
                  <li>The state where your business is located</li>
                  <li>
                    Whether you are using{' '}
                    <span style={{ fontWeight: 600 }}>USPS</span> or a{' '}
                    <span style={{ fontWeight: 600 }}>
                      private delivery service
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Mailing your return to the wrong address can result in:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Late filing penalties</li>
                  <li>Delayed refunds or confirmations</li>
                  <li>IRS correspondence requesting re-submission</li>
                </Box>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  To reduce these risks, many S corporations prefer e-filing
                  Form 1120-S for faster acknowledgment.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="where"
                >
                  Where to File Your Taxes for Form 1120-S
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use the table below to determine the correct{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS mailing address for Form 1120-S
                  </span>
                  . Always match your business location and asset level before
                  sending your return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  IRS Form 1120-S Mailing Address Table
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Corporation’s principal business, office, or agency
                            is located in
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 150,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Total assets at end of tax year
                          </TableCell>
                          <TableCell sx={{ width: 200, fontWeight: 600 }}>
                            IRS mailing address to use
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Less than $10 million{' '}
                            <span style={{ fontWeight: 600 }}>and</span>{' '}
                            Schedule M-3 is{' '}
                            <span style={{ fontWeight: 600 }}>not</span> filed
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                              <br /> Internal Revenue Service
                            </span>
                            <br />
                            Kansas City, MO 64999-0013
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            (Any of the above states)
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            $10 million or more{' '}
                            <span style={{ fontWeight: 600 }}>or</span> Schedule
                            M-3 <span style={{ fontWeight: 600 }}>is</span>{' '}
                            filed
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                              <br />
                              Internal Revenue Service
                            </span>
                            <br />
                            Ogden, UT 84201-0013
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Any amount
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 600 }}>
                              Department of the Treasury
                              <br />
                              Internal Revenue Service
                            </span>
                            <br />
                            Ogden, UT 84201-0013
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            A foreign country or U.S. possession
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Any amount
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 600 }}>
                              Internal Revenue Service
                            </span>
                            <br />
                            P.O. Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="private"
                >
                  Private Delivery Services (PDS) for Form 1120-S
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Corporations may use certain{' '}
                  <span style={{ fontWeight: 600 }}>
                    IRS-designated Private Delivery Services (PDS)
                  </span>{' '}
                  to mail Form 1120-S and still meet the{' '}
                  <span style={{ fontWeight: 600 }}>
                    "timely mailing treated as timely filing"
                  </span>{' '}
                  rule.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Only delivery services officially approved by the IRS qualify
                  under this rule. The current list of designated private
                  delivery services is available on{' '}
                  <Link
                    href="https://www.irs.gov/filing/private-delivery-services-pds"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS PDS site
                  </Link>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  Important PDS Guidelines
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Private delivery services can provide{' '}
                    <span style={{ fontWeight: 600 }}>
                      written proof of the mailing date
                    </span>
                    , which helps establish timely filing
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    When using a PDS, you must send your return to the{' '}
                    <span style={{ fontWeight: 600 }}>IRS street address</span>,
                    not a P.O. Box
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    IRS street addresses for private delivery services are
                    listed at{' '}
                    <Link
                      href="https://www.irs.gov/filing/submission-processing-center-street-addresses-for-private-delivery-service-pds"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS PDS website
                    </Link>
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Private delivery services{' '}
                    <span style={{ fontWeight: 600 }}>
                      cannot deliver to IRS P.O. Boxes
                    </span>
                  </li>
                  <li>
                    Any return addressed to a{' '}
                    <span style={{ fontWeight: 600 }}>
                      P.O. Box must be mailed using the U.S. Postal Service
                      (USPS)
                    </span>
                  </li>
                </ul>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 0, mb: 4 }}
                >
                  <b>Important :</b> Using a P.O. Box address with a private
                  carrier may cause delivery failure or filing delays.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="important"
                >
                  Important Notes for Form 1120-S Mailing
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  To select the correct mailing address:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Identify where your S corporation&apos;s{' '}
                    <span style={{ fontWeight: 600 }}>
                      principal business, office, or agency
                    </span>{' '}
                    is located
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Confirm your{' '}
                    <span style={{ fontWeight: 600 }}>
                      total assets at the end of the tax year
                    </span>
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Check whether{' '}
                    <span style={{ fontWeight: 600 }}>Schedule M-3</span> is
                    required
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Match all three factors with the table above
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Using the wrong address may delay IRS processing or trigger
                  IRS correspondence. If you want to avoid address-related
                  issues entirely,{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-filing Form 1120-S
                  </Link>{' '}
                  is a reliable alternative.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="deadline"
                >
                  Form 1120-S Filing Deadline
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  For most S corporations, the filing deadline is:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  March 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If additional time is needed,{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 7004
                  </Link>{' '}
                  provides an automatic{' '}
                  <span style={{ fontWeight: 600 }}>six-month extension</span>.
                  The extension applies only to the filing deadline, not to any
                  taxes owed.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="mailing"
                >
                  Mailing vs E-Filing Form 1120-S
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  While mailing is allowed, many businesses choose e-filing
                  because it offers:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Faster IRS acknowledgment</li>
                  <li>Reduced risk of filing errors</li>
                  <li>No concern about mailing to the wrong address</li>
                  <li>Quicker overall processing</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  E-filing is generally the preferred method for accuracy and
                  convenience. For more details about filing requirements, refer
                  to the{' '}
                  <Link
                    href="/resources/business-tax/form-1120-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    instructions form 1120 s
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Mailing Mistakes to Avoid
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When filing Form 1120-S by mail, avoid these frequent
                  errors:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Mailing the return to the wrong IRS center</li>
                  <li>
                    Ignoring the{' '}
                    <span style={{ fontWeight: 600 }}>Schedule M-3</span>{' '}
                    requirement
                  </li>
                  <li>
                    Using a P.O. Box address with private delivery services
                  </li>
                  <li>Missing signatures or required schedules</li>
                  <li>Mailing close to the deadline without tracking </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Carefully reviewing the table before mailing can help prevent
                  these issues.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Form 1120-S?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Avoid mailing errors, address confusion, and IRS delays. File your
              Form 1120-S online with a secure, IRS-compliant e-filing solution
              designed specifically for S corporations.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120smail`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120SMailingAddress;
